import React, { useState } from "react";
import { Play, Loader2, ShieldAlert, ScanSearch, ShieldCheck, Ban, FileWarning, LayoutDashboard, ChevronRight } from "lucide-react";
import { track } from "../../lib/analytics";

const ATTEMPTS = [
  {
    id: "injection",
    name: "Prompt injection",
    attempt: "A shared document contains hidden text instructing the assistant to ignore policy and export internal data.",
    detection: "Aegis inspects the retrieved content separately from the user's request and flags an embedded instruction.",
    policy: "Untrusted document instructions are treated as data, not commands.",
    intervention: "The export intent is blocked; the assistant continues without acting on the hidden instruction.",
    severity: "High",
    action: "BLOCK",
  },
  {
    id: "jailbreak",
    name: "Jailbreak attempt",
    attempt: "A user tries a role-play prompt engineered to override system instructions and restricted behaviour.",
    detection: "The input matches manipulation signatures and scores high-risk before reaching the model.",
    policy: "Attempts to override guardrails are refused and recorded.",
    intervention: "The request is blocked and a safe fallback response is returned.",
    severity: "High",
    action: "BLOCK",
  },
  {
    id: "extraction",
    name: "Sensitive-data extraction",
    attempt: "A prompt attempts to coax the assistant into revealing restricted internal information it can access.",
    detection: "Aegis recognises the request pattern and the sensitivity of the target information.",
    policy: "Restricted information is not disclosed outside authorised scope.",
    intervention: "The response is held and routed to an authorised reviewer.",
    severity: "Critical",
    action: "HOLD + ESCALATE",
  },
  {
    id: "circumvention",
    name: "Policy circumvention",
    attempt: "A user rephrases a blocked request repeatedly to find wording that slips past controls.",
    detection: "Repeated near-miss attempts are correlated across the session.",
    policy: "Persistent circumvention triggers a stricter response.",
    intervention: "The session is quarantined and flagged for review.",
    severity: "Medium",
    action: "QUARANTINE",
  },
  {
    id: "malicious",
    name: "Malicious instructions",
    attempt: "An input asks the assistant to generate content that would breach organisational policy.",
    detection: "The intent is classified against your prohibited-use policy.",
    policy: "Prohibited content is refused, with the category recorded.",
    intervention: "The request is blocked and a warning is attached.",
    severity: "High",
    action: "BLOCK",
  },
  {
    id: "manipulated",
    name: "Manipulated AI outputs",
    attempt: "An AI response drifts towards an unsupported or unsafe recommendation.",
    detection: "Aegis inspects the outbound response before it reaches the user or workflow.",
    policy: "Designated output risks are intercepted before release.",
    intervention: "The response is warned or held for human review depending on severity.",
    severity: "Medium",
    action: "WARN / HOLD",
  },
];

const STAGES = [
  { key: "attempt", label: "Attempt", icon: ShieldAlert, tone: "amber" },
  { key: "detection", label: "Detection", icon: ScanSearch, tone: "cyan" },
  { key: "policy", label: "Policy", icon: ShieldCheck, tone: "cyan" },
  { key: "intervention", label: "Intervention", icon: Ban, tone: "red" },
  { key: "incident", label: "Incident", icon: FileWarning, tone: "amber" },
  { key: "review", label: "Review", icon: LayoutDashboard, tone: "cyan" },
];

const toneText = { amber: "text-signal-amber", cyan: "text-primary", red: "text-signal-red" };
const toneBorder = { amber: "border-signal-amber/40 bg-signal-amber/[0.06]", cyan: "border-primary/40 bg-primary/[0.06]", red: "border-signal-red/40 bg-signal-red/[0.06]" };

export default function AdversarialDemo() {
  const [active, setActive] = useState(ATTEMPTS[0].id);
  const [running, setRunning] = useState(false);
  const [revealed, setRevealed] = useState(0);
  const cur = ATTEMPTS.find((a) => a.id === active);

  const run = () => {
    setRunning(true);
    setRevealed(0);
    track("defence_adversarial_run", { attempt: active });
    let i = 0;
    const tick = () => {
      i += 1;
      setRevealed(i);
      if (i >= STAGES.length) { setRunning(false); return; }
      setTimeout(tick, 420);
    };
    setTimeout(tick, 300);
  };

  const stageBody = (key) => {
    switch (key) {
      case "attempt": return cur.attempt;
      case "detection": return cur.detection;
      case "policy": return cur.policy;
      case "intervention": return cur.intervention;
      case "incident": return `Synthetic Heimdall incident opened · severity ${cur.severity} · action ${cur.action}.`;
      case "review": return "Routed to an authorised human reviewer for a final, evidenced decision.";
      default: return "";
    }
  };

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-4" data-testid="defence-adversarial">
      <div>
        <div className="flex flex-col gap-1.5">
          {ATTEMPTS.map((a) => {
            const on = a.id === active;
            return (
              <button
                key={a.id}
                onClick={() => { setActive(a.id); setRevealed(0); setRunning(false); }}
                data-testid={`defence-attack-${a.id}`}
                className={`flex items-center justify-between gap-2 rounded-xl border px-4 py-3 text-left text-sm transition-colors ${
                  on ? "border-primary/40 bg-primary/[0.07] text-foreground" : "border-white/10 bg-navy-900/60 text-muted-foreground hover:border-white/20"
                }`}
              >
                {a.name}
                {on && <ChevronRight className="h-4 w-4 text-primary" />}
              </button>
            );
          })}
        </div>
        <button
          onClick={run}
          disabled={running}
          data-testid="defence-run-attack"
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-70"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Run a simulated attack
        </button>
        <p className="mt-2 text-[0.7rem] text-muted-foreground/70">Synthetic examples only. Nothing here enables a real attack.</p>
      </div>

      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-5 sm:p-6">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <p className="text-sm font-semibold text-foreground">{cur.name}</p>
          <span className="rounded-full border border-signal-amber/40 bg-signal-amber/[0.06] px-2.5 py-0.5 text-[0.65rem] font-semibold text-signal-amber">SEVERITY {cur.severity.toUpperCase()}</span>
        </div>
        <div className="mt-4 space-y-2">
          {STAGES.map((s, i) => {
            const Icon = s.icon;
            const shown = revealed > i;
            return (
              <div
                key={s.key}
                className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition-all duration-300 ${
                  shown ? toneBorder[s.tone] + " opacity-100" : "border-white/8 bg-navy-800/40 opacity-45"
                }`}
              >
                <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.04] ${shown ? toneText[s.tone] : "text-muted-foreground/50"}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className={`block text-xs font-semibold uppercase tracking-wide ${shown ? toneText[s.tone] : "text-muted-foreground/60"}`}>{s.label}</span>
                  <span className="mt-0.5 block text-sm text-foreground/85">{shown ? stageBody(s.key) : "—"}</span>
                </span>
              </div>
            );
          })}
        </div>
        {revealed >= STAGES.length && (
          <p className="mt-4 text-xs text-muted-foreground">Connected to the same Heimdall oversight demonstrated elsewhere on the platform — every intervention becomes a reviewable record.</p>
        )}
      </div>
    </div>
  );
}
